import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FacilityOption } from 'components/inboundReferrals/constants';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import Tabs, { TabType } from 'components/Tabs';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useProfile } from 'context/profile';
import { PERMISSIONS } from 'models/Profile';
import { adminShowClient } from 'services/api/admin/clients';
import { adminGroupsQueryKeys, adminIndexGroups } from 'services/api/admin/groups';
import {
  ClientPortalSystemOptions,
  clientPortalSystemQueryKeys,
  indexClientPortalSystems,
  RetrievalMode,
  revokePortalAutomationCredential,
  ScriptSharing,
  upsertClientPortalSystem,
} from 'services/api/clientPortalSystems';
import {
  deletePortalMapping,
  FACILITY_MAPPING_NAME,
  indexPortalMappings,
  PortalMappingOptions,
  portalMappingQueryKeys,
  rerunBlockedPortalMappingWorkflows,
  upsertPortalMapping,
} from 'services/api/portalMappings';
import {
  AdminPortalScript,
  adminPortalScriptQueryKeys,
  indexAdminPortalScripts,
  PortalScriptPurpose,
  updateAdminPortalScript,
  UpdateAdminPortalScriptInput,
} from 'services/api/portalScripts';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

// Both portal-automation knobs are a low → high ladder of named levels, each with a
// line explaining what it means.
type LadderOption<T extends string> = { id: T; name: string; description: string };

// The retrieval-mode ladder: each mode falls back to the one below it.
const RETRIEVAL_MODE_OPTIONS: LadderOption<RetrievalMode>[] = [
  { id: 'manual', name: 'Manual', description: 'A person reads the portal and types the details in.' },
  {
    id: 'extension',
    name: 'Extension',
    description: "The Olio browser extension retrieves in the user's browser, with the manual form as fallback.",
  },
  {
    id: 'headless',
    name: 'Headless',
    description:
      'Olio signs in and retrieves automatically with saved credentials, falling back to the extension or manual form.',
  },
];

// Script sharing is per source system — a script only replays in the portal it was
// recorded against, so it never travels between systems at any level.
const SCRIPT_SHARING_OPTIONS: LadderOption<ScriptSharing>[] = [
  { id: 'own_only', name: 'Own scripts only', description: 'Each user must record their own scripts.' },
  { id: 'client', name: 'Within this client', description: "This client's users borrow each other's scripts." },
  {
    id: 'inter_client',
    name: 'Across clients',
    description:
      'Also borrow from other clients that allow it. Sharing is reciprocal — a script is only borrowable from a client that has this set too, so users still record their own when nothing matches.',
  },
];

// Retrieval scripts pull one referral's records; discovery scripts sweep the portal
// for new referral activity; collection scripts are the extension's auto-fill — read-only
// locators the standing card evaluates on a page the user is already looking at. Rows
// group by purpose so an admin renaming or archiving can tell them apart when several
// exist for the same source system.
const PURPOSE_ORDER: PortalScriptPurpose[] = ['retrieval', 'discovery', 'collection'];
const PURPOSE_LABELS: Record<PortalScriptPurpose, string> = {
  retrieval: 'Retrieval',
  discovery: 'Discovery',
  collection: 'Auto-fill',
};

// The TS type says every row carries a known purpose, but that's a compile-time assertion
// over whatever the api actually served — a row with a missing or unrecognized purpose
// (legacy payload, future third purpose) must still be manageable here, not silently
// dropped from the only surface that can rename/archive it.
export function purposeLabel(purpose: string | undefined): string {
  return PURPOSE_LABELS[purpose as PortalScriptPurpose] ?? 'Other';
}

type ScriptGrouping = { key: string; header: string; group: AdminPortalScript[] };

function groupedByPurpose(scripts: AdminPortalScript[]): ScriptGrouping[] {
  const known: ScriptGrouping[] = PURPOSE_ORDER.map((purpose) => ({
    key: purpose,
    header: PURPOSE_LABELS[purpose],
    group: scripts.filter((s) => s.purpose === purpose),
  }));
  const other = scripts.filter((s) => !PURPOSE_ORDER.includes(s.purpose));
  return [...known, { key: 'other', header: 'Other', group: other }].filter(({ group }) => group.length > 0);
}

type SystemForm = { retrievalMode: RetrievalMode; scriptSharing: ScriptSharing };
type Forms = Record<string, SystemForm>;

function deepEqual(a: unknown, b: unknown) {
  return JSON.stringify(a) === JSON.stringify(b);
}

// olio-api renders authorization failures as { message } and validation failures
// as { errors: { field: [reasons] } } (OliveBranch camelCases the field keys).
// Surface either so a rejected save is actionable — e.g. an api that doesn't yet
// accept a newly-offered mode 422s with a reason, not a shrug.
function saveErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data?.message === 'string') return data.message;
    const [field, reasons] = Object.entries(data?.errors ?? {})[0] ?? [];
    if (field && Array.isArray(reasons) && typeof reasons[0] === 'string') {
      const fieldLabel = field.replace(/([A-Z])/g, ' $1').toLowerCase();
      return `Could not save: ${fieldLabel} ${reasons[0]}.`;
    }
  }
  return 'There was an error saving the portal automation settings.';
}

// The API rides the admin_client permissions (Admin::PortalAutomationSettingPolicy /
// Admin::PortalScriptPolicy): adminClientView to read, adminClientEdit to write.
// View-only users get the page with every field disabled, per admin convention.
export default function PortalAutomationSettings() {
  const { id } = useParams<{ id: string }>();
  const { profile } = useProfile();
  const { addToast } = useToastActions();
  const queryClient = useQueryClient();
  const disabled = !profile.has(PERMISSIONS.adminClientEdit);

  const clientQuery = useQuery({
    queryKey: ['adminClient', id],
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });
  const settingsQuery = useQuery({
    queryKey: clientPortalSystemQueryKeys.list(id!),
    queryFn: ({ signal }) => indexClientPortalSystems(id!, signal),
    enabled: !!id,
  });
  const scriptsQuery = useQuery({
    queryKey: adminPortalScriptQueryKeys.list(id!),
    queryFn: ({ signal }) => indexAdminPortalScripts(id!, signal),
    enabled: !!id,
  });
  const mappingsQuery = useQuery({
    queryKey: portalMappingQueryKeys.list(id!),
    queryFn: ({ signal }) => indexPortalMappings(id!, signal),
    enabled: !!id,
  });
  // The facility choices a mapping can point at — the client's skilled nursing groups.
  const providerGroupsQuery = useQuery({
    queryKey: [...adminGroupsQueryKeys.index, 'providers', id],
    queryFn: ({ signal }) => adminIndexGroups({ clientId: id!, type: SKILLED_NURSING_FACILITY, pageSize: 200 }, signal),
    enabled: !!id,
  });

  const serverForms = useRef<Forms>({});
  const [forms, setForms] = useState<Forms>({});
  const [selectedTabId, setSelectedTabId] = useState<string>('');

  // One settings block per connection (OLIO-3073) — the rows ARE the client's portals,
  // so an unconnected system gets no tab. Connections are managed on the Edit Client page.
  const connections = settingsQuery.data?.data ?? [];

  useEffect(() => {
    if (!settingsQuery.data) return;

    const built: Forms = {};
    for (const row of settingsQuery.data.data) {
      built[row.sourceSystem] = {
        retrievalMode: row.retrievalMode,
        scriptSharing: row.scriptSharing,
      };
    }
    serverForms.current = built;
    setForms(built);
    setSelectedTabId((current) =>
      settingsQuery.data.data.some((row) => row.sourceSystem === current)
        ? current
        : (settingsQuery.data.data[0]?.sourceSystem ?? '')
    );
  }, [settingsQuery.data]);

  const isDirty = !deepEqual(forms, serverForms.current);

  const { mutate: save, isPending } = useMutation({
    mutationFn: async (updated: Forms) => {
      const dirty = Object.entries(updated).filter(([sys, form]) => !deepEqual(form, serverForms.current[sys]));

      await Promise.all(
        dirty.map(([sourceSystem, form]) => {
          // Only the knobs this admin actually moved. Sending the whole form would write back
          // a stale snapshot of the untouched knob, silently reverting a change someone else
          // made since this page loaded. The api upserts field-by-field, so omitting one
          // leaves it alone.
          const baseline = serverForms.current[sourceSystem];
          return upsertClientPortalSystem({
            clientId: id!,
            sourceSystem,
            ...(form.retrievalMode !== baseline?.retrievalMode && { retrievalMode: form.retrievalMode }),
            ...(form.scriptSharing !== baseline?.scriptSharing && { scriptSharing: form.scriptSharing }),
          });
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientPortalSystemQueryKeys.list(id!) });
      addToast({ text: 'Portal automation settings saved.' });
    },
    onError: (error) => addToast({ text: saveErrorMessage(error) }),
  });

  const handleDiscard = () => setForms({ ...serverForms.current });

  const tabs: TabType[] = connections.map((row) => ({
    label: row.displayName,
    value: row.sourceSystem,
  }));
  const selectedTab = tabs.find((t) => t.value === selectedTabId);
  const selectedForm = forms[selectedTabId];
  const selectedConnection = connections.find((s) => s.sourceSystem === selectedTabId);

  if (settingsQuery.isPending || clientQuery.isPending) {
    return <CircularProgress style={{ margin: '48px auto', display: 'block' }} />;
  }

  return (
    <PageContainer>
      <Header>
        <HeaderTop>
          <TitleArea>
            <Title>Portal Automation</Title>
            <Subtitle>{clientQuery.data?.name}</Subtitle>
          </TitleArea>
          {!disabled && (
            <ButtonGroup>
              <Button variant='ghost' onClick={handleDiscard} disabled={!isDirty || isPending}>
                Discard Changes
              </Button>
              <Button data-testid='portal-automation-save' onClick={() => save(forms)} disabled={!isDirty || isPending}>
                Save Changes
              </Button>
            </ButtonGroup>
          )}
        </HeaderTop>
        {tabs.length > 1 && (
          <TabsWrapper data-testid='portal-automation-tabs'>
            <Tabs tabs={tabs} selectedTab={selectedTab} onTabClick={(tab) => setSelectedTabId(tab.value)} />
          </TabsWrapper>
        )}
      </Header>

      <Content>
        {settingsQuery.isError && <ErrorText>Portal automation settings failed to load.</ErrorText>}
        {!settingsQuery.isError && connections.length === 0 && (
          <EmptyState data-testid='portal-automation-empty'>
            This client isn&apos;t connected to any portals. Add portal systems from the client&apos;s edit page.
          </EmptyState>
        )}
        {connections.length > 0 && selectedForm && id && (
          <SourceSystemSettings
            key={selectedTabId}
            clientId={id}
            sourceSystem={selectedTabId}
            label={selectedConnection?.displayName ?? selectedTabId}
            setting={selectedConnection}
            form={selectedForm}
            onChange={(updated) => setForms((prev) => ({ ...prev, [selectedTabId]: updated }))}
            scripts={(scriptsQuery.data ?? []).filter((s) => s.sourceSystem === selectedTabId)}
            mappings={(mappingsQuery.data?.data ?? []).filter(
              (m) => m.sourceSystem === selectedTabId && m.name === FACILITY_MAPPING_NAME
            )}
            facilityOptions={(providerGroupsQuery.data?.data ?? []).map((group) => ({
              id: group.id!,
              name: group.name ?? group.id!,
            }))}
            disabled={disabled}
          />
        )}
      </Content>
    </PageContainer>
  );
}

type SourceSystemProps = {
  clientId: string;
  sourceSystem: string;
  label: string;
  setting?: ClientPortalSystemOptions;
  form: SystemForm;
  onChange: (form: SystemForm) => void;
  scripts: AdminPortalScript[];
  mappings: PortalMappingOptions[];
  facilityOptions: FacilityOption[];
  disabled: boolean;
};

function SourceSystemSettings({
  clientId,
  sourceSystem,
  label,
  setting,
  form,
  onChange,
  scripts,
  mappings,
  facilityOptions,
  disabled,
}: SourceSystemProps) {
  return (
    <SystemSection data-testid={`portal-automation-${sourceSystem}`}>
      <InputGroup title='Retrieval Mode'>
        <LadderSelect
          inputId={`retrieval-mode-${sourceSystem}`}
          options={RETRIEVAL_MODE_OPTIONS}
          value={form.retrievalMode}
          onChange={(retrievalMode) => onChange({ ...form, retrievalMode })}
          disabled={disabled}
        />
      </InputGroup>

      <InputGroup title='Script Sharing'>
        <LadderSelect
          inputId={`script-sharing-${sourceSystem}`}
          options={SCRIPT_SHARING_OPTIONS}
          value={form.scriptSharing}
          onChange={(scriptSharing) => onChange({ ...form, scriptSharing })}
          disabled={disabled}
        />
      </InputGroup>
      <MutedNote>Scripts are specific to {label} and are never shared with another portal.</MutedNote>

      <ScriptsBlock data-testid={`portal-credentials-${sourceSystem}`}>
        <FieldLabel>Headless sign-in</FieldLabel>
        <HeadlessCredentialStatus
          clientId={clientId}
          sourceSystem={sourceSystem}
          setting={setting}
          label={label}
          headlessSelected={form.retrievalMode === 'headless'}
          disabled={disabled}
        />
      </ScriptsBlock>

      <ScriptsBlock data-testid={`portal-scripts-${sourceSystem}`}>
        <FieldLabel>Recorded scripts</FieldLabel>
        {scripts.length === 0 && <MutedNote>No scripts recorded for {label} yet.</MutedNote>}
        {groupedByPurpose(scripts).map(({ key, header, group }) => (
          <ScriptGroup key={key} data-testid={`portal-scripts-${sourceSystem}-${key}`}>
            <GroupHeader>{header}</GroupHeader>
            {group.map((script) => (
              <ScriptRow key={script.id} clientId={clientId} script={script} disabled={disabled} />
            ))}
          </ScriptGroup>
        ))}
      </ScriptsBlock>

      <MappingsBlock
        clientId={clientId}
        sourceSystem={sourceSystem}
        label={label}
        mappings={mappings}
        facilityOptions={facilityOptions}
        disabled={disabled}
      />
    </SystemSection>
  );
}

type LadderSelectProps<T extends string> = {
  inputId: string;
  options: LadderOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled: boolean;
};

// A stored level this build doesn't recognize renders as unselected. Falling back to a known
// option would show a level that is not the one that would save — worse than showing none,
// because the admin would read it as confirmation.
function LadderSelect<T extends string>({ inputId, options, value, onChange, disabled }: LadderSelectProps<T>) {
  const selected = options.find((o) => o.id === value) ?? null;

  return (
    <Select<LadderOption<T>>
      inputId={inputId}
      options={options}
      value={selected}
      onChange={(o) => o && onChange(o.id)}
      getOptionValue={(o) => o.id}
      getOptionLabel={(o) => o.name}
      isDisabled={disabled}
      formatOptionLabel={(o, meta) => {
        if (!o) return null;
        if (meta?.context === 'value') return o.name;
        return (
          <div>
            <div>{o.name}</div>
            <div style={{ fontSize: '0.9em', color: colors.black50 }}>{o.description}</div>
          </div>
        );
      }}
    />
  );
}

type MappingsBlockProps = {
  clientId: string;
  sourceSystem: string;
  label: string;
  mappings: PortalMappingOptions[];
  facilityOptions: FacilityOption[];
  disabled: boolean;
};

// The tab's facility mappings: how this portal's facility strings resolve to Olio
// facilities. Rows are usually born from the workflow's mapping step (a person
// resolving a halted run); this surface is for review and correction.
function MappingsBlock({ clientId, sourceSystem, label, mappings, facilityOptions, disabled }: MappingsBlockProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [externalValue, setExternalValue] = useState('');
  const [facility, setFacility] = useState<FacilityOption | null>(null);
  // A just-saved row that can move parked runs (OLIO-3130). The count comes off the save's own
  // response, so a save that unblocks nothing offers nothing.
  const [rerunOffer, setRerunOffer] = useState<{ mapping: PortalMappingOptions; count: number } | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: portalMappingQueryKeys.list(clientId) });

  const upsert = useMutation({
    mutationFn: () =>
      upsertPortalMapping({
        clientId,
        sourceSystem,
        name: FACILITY_MAPPING_NAME,
        externalValue: externalValue.trim(),
        mappedValue: facility!.id,
      }),
    onSuccess: (mapping) => {
      refresh();
      setExternalValue('');
      setFacility(null);
      const count = mapping.blockedWorkflowsCount ?? 0;
      setRerunOffer(count > 0 ? { mapping, count } : null);
    },
    onError: () => addToast({ text: 'There was an error saving the mapping.' }),
  });

  const rerun = useMutation({
    mutationFn: (mappingId: string) => rerunBlockedPortalMappingWorkflows(mappingId, clientId),
    onSuccess: (mapping) => {
      addToast({
        text: `Re-running ${mapping.rerunCount ?? 0} blocked inbound referral${(mapping.rerunCount ?? 0) === 1 ? '' : 's'}.`,
      });
      setRerunOffer(null);
    },
    onError: () => addToast({ text: 'There was an error re-running the blocked inbound referrals.' }),
  });

  const remove = useMutation({
    mutationFn: (mappingId: string) => deletePortalMapping(mappingId, clientId),
    onSuccess: refresh,
    onError: () => addToast({ text: 'There was an error removing the mapping.' }),
  });

  const addReady = externalValue.trim().length > 0 && !!facility && !upsert.isPending;

  return (
    <MappingsSection data-testid={`portal-mappings-${sourceSystem}`}>
      <FieldLabel>Facility mappings</FieldLabel>
      <MutedNote>How facility names in {label} map to your Olio facilities.</MutedNote>
      {mappings.length === 0 && <MutedNote>No mappings for {label} yet.</MutedNote>}
      {mappings.map((mapping) => (
        <MappingRow key={mapping.id} data-testid={`portal-mapping-${mapping.id}`}>
          <MappingExternal>{mapping.externalValue}</MappingExternal>
          <MappingArrow>→</MappingArrow>
          <MappingTarget>{mapping.mappedLabel ?? mapping.mappedValue}</MappingTarget>
          {!disabled && (
            <MappingDelete
              type='button'
              aria-label='Remove mapping'
              onClick={() => remove.mutate(mapping.id)}
              disabled={remove.isPending}
              data-testid={`portal-mapping-delete-${mapping.id}`}>
              ×
            </MappingDelete>
          )}
        </MappingRow>
      ))}
      {rerunOffer && (
        <RerunOfferRow data-testid={`portal-mapping-rerun-offer-${sourceSystem}`}>
          <MutedNote>
            {rerunOffer.count} referral{rerunOffer.count === 1 ? ' is' : 's are'} blocked on “
            {rerunOffer.mapping.externalValue}” and can now be worked automatically.
          </MutedNote>
          <Button
            size='small'
            onClick={() => rerun.mutate(rerunOffer.mapping.id)}
            disabled={rerun.isPending}
            data-testid={`portal-mapping-rerun-${sourceSystem}`}>
            {rerun.isPending ? 'Re-running…' : `Re-run ${rerunOffer.count === 1 ? 'it' : 'them'}`}
          </Button>
          <Button
            size='small'
            variant='ghost'
            onClick={() => setRerunOffer(null)}
            data-testid={`portal-mapping-rerun-dismiss-${sourceSystem}`}>
            Dismiss
          </Button>
        </RerunOfferRow>
      )}
      {!disabled && (
        <MappingAddRow>
          <Input
            placeholder={`Facility name in ${label}`}
            value={externalValue}
            onChange={(e) => setExternalValue(e.target.value)}
            data-testid={`portal-mapping-external-${sourceSystem}`}
          />
          <MappingSelectCell>
            <Select<FacilityOption>
              inputId={`portal-mapping-facility-${sourceSystem}`}
              placeholder='Olio facility'
              options={facilityOptions}
              value={facility}
              onChange={setFacility}
              menuPortalTarget={document.body}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
            />
          </MappingSelectCell>
          <Button
            size='small'
            onClick={() => upsert.mutate()}
            disabled={!addReady}
            data-testid={`portal-mapping-add-${sourceSystem}`}>
            Add
          </Button>
        </MappingAddRow>
      )}
    </MappingsSection>
  );
}

// Mirror of the integration platform's credential state (OLIO-2887's status line)
// plus the revoke kill-switch (OLIO-2907). The mirror is a cache — the integration
// platform re-checks the real secret at run time. Selecting headless with nothing
// saved is allowed but annotated: runs fall back down the ladder until a user
// supplies credentials from the retrieval card.
function HeadlessCredentialStatus({
  clientId,
  sourceSystem,
  setting,
  label,
  headlessSelected,
  disabled,
}: {
  clientId: string;
  sourceSystem: string;
  setting?: ClientPortalSystemOptions;
  label: string;
  headlessSelected: boolean;
  disabled: boolean;
}) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [confirming, setConfirming] = useState(false);

  const revoke = useMutation({
    mutationFn: () => revokePortalAutomationCredential({ clientId, sourceSystem }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientPortalSystemQueryKeys.list(clientId) });
      addToast({ text: `Sign-in for ${label} was revoked.` });
      setConfirming(false);
    },
    // A failed revoke may mean the credential still exists (the integration
    // platform 502s when the delete could not be confirmed) — say so plainly.
    onError: () => addToast({ text: `Could not revoke the ${label} sign-in — it may still be stored. Try again.` }),
  });

  const status = setting?.headlessCredentialsStatus;
  if (!status) {
    if (headlessSelected) {
      return (
        <MutedNote data-testid='headless-credentials-waiting'>
          Waiting for a user to set up credentials from Manage Connected Portals — runs will use the extension/manual
          flow until then.
        </MutedNote>
      );
    }
    return <MutedNote data-testid='headless-credentials-none'>No sign-in saved for automatic retrieval.</MutedNote>;
  }

  const username = setting?.headlessCredentialsUsername ?? 'unknown user';
  const ownerEmail = setting?.headlessUserEmail;
  const savedAt = setting?.headlessCredentialsSavedAt;
  const verifiedAt = setting?.headlessCredentialsVerifiedAt;
  const saved = savedAt ? ` saved ${new Date(savedAt).toLocaleDateString()}` : '';

  const revokeControl = disabled ? null : confirming ? (
    <RevokeConfirm data-testid='headless-credentials-revoke-confirm'>
      <MutedNote>Revoke this sign-in? It is deleted permanently and cannot be recovered.</MutedNote>
      <ButtonGroup>
        <Button
          variant='danger'
          size='small'
          loading={revoke.isPending}
          onClick={() => revoke.mutate()}
          data-testid='headless-credentials-revoke-confirm-button'>
          Revoke
        </Button>
        <Button
          variant='outline-gray'
          size='small'
          disabled={revoke.isPending}
          onClick={() => setConfirming(false)}
          data-testid='headless-credentials-revoke-cancel'>
          Cancel
        </Button>
      </ButtonGroup>
    </RevokeConfirm>
  ) : (
    <Button variant='danger' size='small' onClick={() => setConfirming(true)} data-testid='headless-credentials-revoke'>
      Revoke sign-in
    </Button>
  );

  return (
    <CredentialBlock>
      {status === 'invalid' ? (
        <InvalidNote data-testid='headless-credentials-invalid'>
          {username} —{saved}, but {label} rejected it. It needs to be saved again from the retrieval card.
        </InvalidNote>
      ) : (
        <MutedNote data-testid='headless-credentials-status'>
          {username} —{saved},{' '}
          {verifiedAt ? `verified ${new Date(verifiedAt).toLocaleDateString()}` : 'not yet verified'}
        </MutedNote>
      )}
      {/* Who saved it — whose script drives dispatch, and whose deactivation stops
          headless (OLIO-2949). Distinct from the portal username: they diverge on
          shared portal accounts. Absent when the owner's user row is gone. */}
      {ownerEmail && <MutedNote data-testid='headless-credentials-owner'>Owned by {ownerEmail}</MutedNote>}
      {revokeControl}
    </CredentialBlock>
  );
}

type ScriptRowProps = {
  clientId: string;
  script: AdminPortalScript;
  disabled: boolean;
};

// One recorded script: rename inline or archive/restore. Bodies never reach the
// admin surface — a script's steps change only by re-recording in the extension.
function ScriptRow({ clientId, script, disabled }: ScriptRowProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(script.name);

  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const mutation = useMutation({
    mutationFn: (patch: Omit<UpdateAdminPortalScriptInput, 'id'>) =>
      updateAdminPortalScript({ id: script.id, ...patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminPortalScriptQueryKeys.list(clientId) });
      setEditing(false);
    },
    onError: () => addToast({ text: 'There was an error updating the script.' }),
  });

  const archived = !!script.archivedAt;
  return (
    <ScriptRowContainer data-testid={`portal-script-${script.id}`} $archived={archived}>
      <ScriptMain>
        {editing ? (
          <ScriptRenameRow>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`script-name-${script.id}`} />
            <Button
              size='small'
              onClick={() => name.trim() && mutation.mutate({ name: name.trim() })}
              disabled={mutation.isPending}>
              Save
            </Button>
            <Button size='small' variant='ghost' onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </ScriptRenameRow>
        ) : (
          <>
            <ScriptName>{script.name}</ScriptName>
            <PurposeBadge $purpose={script.purpose} data-testid={`script-purpose-${script.id}`}>
              {purposeLabel(script.purpose)}
            </PurposeBadge>
            {archived && <ArchivedBadge>Archived</ArchivedBadge>}
          </>
        )}
        <ScriptMeta>
          {script.user.email} · updated {new Date(script.updatedAt).toLocaleDateString()}
        </ScriptMeta>
      </ScriptMain>
      {!editing && !disabled && (
        <ScriptActions>
          <ScriptActionButton onClick={() => setEditing(true)} disabled={mutation.isPending}>
            Rename
          </ScriptActionButton>
          <ScriptActionButton
            onClick={() => mutation.mutate({ archived: !archived })}
            disabled={mutation.isPending}
            data-testid={`script-archive-${script.id}`}>
            {archived ? 'Restore' : 'Archive'}
          </ScriptActionButton>
        </ScriptActions>
      )}
    </ScriptRowContainer>
  );
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  flex: 0 0 auto;
  background: white;
  border-bottom: 1px solid ${colors.black10};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 24px 16px;
`;

const TitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: ${colors.black100};
`;

const Subtitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: ${colors.blue100};
`;

const TabsWrapper = styled.div`
  border-top: 1px solid ${colors.black10};
  padding: 0 24px;
`;

const Content = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px;
`;

const SystemSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 560px;
`;

const InvalidNote = styled.p`
  font-size: 13px;
  color: ${colors.red100};
  margin: 4px 0 0;
`;

const MutedNote = styled.p`
  font-size: 12px;
  color: ${colors.black50};
  margin: 0;
`;

const CredentialBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const RevokeConfirm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ScriptsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const MappingsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const MappingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
`;

const MappingExternal = styled.span`
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const MappingArrow = styled.span`
  color: ${colors.black50};
  font-size: 12px;
`;

const MappingTarget = styled.span`
  font-size: 13px;
  overflow-wrap: anywhere;
`;

const MappingDelete = styled.button`
  margin-left: auto;
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: ${colors.black50};
  font-size: 16px;
  line-height: 1;
`;

const MappingAddRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RerunOfferRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
`;

const MappingSelectCell = styled.div`
  flex: 0 0 220px;
`;

const ScriptRowContainer = styled.div<{ $archived: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  opacity: ${({ $archived }) => ($archived ? 0.6 : 1)};
`;

const ScriptMain = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const ScriptName = styled.span`
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ScriptMeta = styled.span`
  flex-basis: 100%;
  font-size: 11px;
  color: ${colors.black50};
`;

const ScriptRenameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ScriptGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupHeader = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.black50};
  margin-top: 4px;
`;

// Neutral for anything that isn't a known purpose — the row still renders (see
// groupedByPurpose), the badge just doesn't pretend to know what it is.
const PurposeBadge = styled.span<{ $purpose: string }>`
  font-size: 11px;
  color: ${({ $purpose }) =>
    $purpose === 'discovery' ? colors.purple100 : $purpose === 'retrieval' ? colors.blue100 : colors.black50};
  border: 1px solid
    ${({ $purpose }) =>
      $purpose === 'discovery' ? colors.purple50 : $purpose === 'retrieval' ? colors.blue50 : colors.black25};
  border-radius: 10px;
  padding: 1px 8px;
`;

const ArchivedBadge = styled.span`
  font-size: 11px;
  color: ${colors.black50};
  border: 1px solid ${colors.black25};
  border-radius: 10px;
  padding: 1px 8px;
`;

const ScriptActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

const ScriptActionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: ${colors.blue100};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: ${colors.black25};
    cursor: default;
  }
`;

const EmptyState = styled.p`
  margin: 24px 0;
  color: ${colors.black50};
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: ${colors.red100};
  margin: 0;
`;
